// ─── EditorGuides ─────────────────────────────────────────────
// CSS-only overlay shown inside the editor canvas only.
// pointer-events: none everywhere — guides never block dragging.
// Never rendered on the public page.
//
// Shows:
//   • center vertical + horizontal lines
//   • safe-area dashed rectangle
//   • mobile viewport-fold markers (390 × 844 reference) with labels —
//     positioned as % of the section's *logical* height so designers can
//     instantly see whether content sits above or below the phone fold.

import React from 'react';

// Logical phone viewport reference used across the editor + checker.
// Matches `mobileLayoutHealthCheck` and what Studio designs against.
export const MOBILE_VIEWPORT_HEIGHT_PX = 844;

export default function EditorGuides({ enabled, sectionHeightPx }) {
  if (!enabled) return null;

  // Compute fold-line positions. We only show folds that fit inside the
  // section so we don't pollute short canvases with off-screen markers.
  const folds = [];
  if (sectionHeightPx && sectionHeightPx > MOBILE_VIEWPORT_HEIGHT_PX) {
    const max = Math.min(20, Math.floor(sectionHeightPx / MOBILE_VIEWPORT_HEIGHT_PX));
    for (let n = 1; n <= max; n++) {
      const px = n * MOBILE_VIEWPORT_HEIGHT_PX;
      if (px >= sectionHeightPx) break;
      folds.push({ n, px, topPct: (px / sectionHeightPx) * 100 });
    }
  }

  return (
    <div
      className="editor-guides-layer"
      aria-hidden="true"
      style={{
        position: 'absolute', inset: 0,
        pointerEvents: 'none',
        zIndex: 999,
      }}
      data-testid="editor-guides"
    >
      {/* Center vertical line */}
      <div className="editor-guide center-x" />
      {/* Center horizontal line */}
      <div className="editor-guide center-y" />
      {/* Safe-area dashed rectangle */}
      <div className="editor-safe-area" />
      {/* Mobile viewport fold markers */}
      {folds.map((f) => (
        <React.Fragment key={f.n}>
          <div
            className="editor-viewport-fold"
            style={{ top: `${f.topPct}%` }}
            data-testid={`editor-viewport-fold-${f.n}`}
          />
          <div
            className="editor-viewport-fold-label"
            style={{ top: `${f.topPct}%` }}
          >
            {f.px}px · {f.n} screen{f.n === 1 ? '' : 's'}
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}
